import { ReactNode } from 'react'
import { cn } from 'app/lib/utils'

export type TableEmptyStateProps = {
  /** Bare icon element. This component supplies the medallion around it. */
  icon?: ReactNode
  title: string
  description?: string
  /** Optional call to action, typically a create button. */
  action?: ReactNode
  /**
   * `positive` when an empty table is good news rather than a gap to fill, for
   * example a work queue the team has cleared. Tints the medallion so the state
   * reads as a win at a glance. Defaults to `neutral`.
   */
  tone?: `neutral` | `positive`
  className?: string
}

const MEDALLION_TONES = {
  neutral: `bg-surface-muted text-subtle`,
  positive: `bg-green-50 text-green-600`,
} as const

/**
 * The empty body of a table. Unlike `EmptyState` it draws no border of its own:
 * it renders inside the table frame, which already has one, so the frame and the
 * column headers stay put and the layout does not jump when rows arrive.
 */
export function TableEmptyState({
  icon,
  title,
  description,
  action,
  tone = `neutral`,
  className,
}: TableEmptyStateProps) {
  return (
    <div className={cn(`flex h-full min-h-70 flex-col items-center justify-center px-6 py-14 text-center`, className)}>
      {icon != null && <div className={cn(`mb-3 rounded-full p-3`, MEDALLION_TONES[tone])}>{icon}</div>}
      <div className="text-body-sm font-semibold tracking-tight text-strong">{title}</div>
      {description != null && <p className="mt-1 max-w-md text-body-sm text-subtle">{description}</p>}
      {action != null && <div className="mt-4">{action}</div>}
    </div>
  )
}
